import { legit } from "apex-legit-service";
import { ApexAuth, AuthorizationRequestData } from "apex-ui-services";
import isNil from "lodash.isnil";
import React, { createContext, useEffect } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { RootState } from "StoreTypes";
import { AuthorizationState } from "types";
import {
  ALL_REQUESTED_PERMISSIONS,
  convertToAuthRequest,
  LEGIT_SERVICE_CONFIG,
} from "../../../constants";
import { apiToken } from "../../../services/token";

export interface AuthContextProps {
  handleSignOut: () => void;
  showAppContent: boolean;
  authToken: string;
}

export interface AuthProviderProps {
  getAuth: (payload: AuthorizationRequestData[]) => void;
  auth: AuthorizationState;
  getLegacyUrl: () => void;
  showAppContent: boolean;
  logout: (params: { apexUser: boolean }) => void;
  clearLoginType: () => void;
  token: string | null;
  children: JSX.Element | JSX.Element[];
}

export const AuthContext = createContext<AuthContextProps | {}>({});

export const AuthProviderComponent = ({
  children,
  getAuth,
  auth,
  getLegacyUrl,
  showAppContent,
  logout,
  clearLoginType,
  token,
}: AuthProviderProps) => {
  const authToken = token as string;

  useEffect(() => {
    if (isNil(auth?.authorization) && !auth?.hasBeenAsked) {
      getAuth(convertToAuthRequest(ALL_REQUESTED_PERMISSIONS));
    }

    getLegacyUrl();
  }, [auth, getAuth, getLegacyUrl]);

  const handleSignOut = () => {
    if (showAppContent) {
      const params = { apexUser: false };
      logout(params);
      clearLoginType();
    }
  };

  return (
    <AuthContext.Provider value={{ handleSignOut, showAppContent, authToken }}>
      {children}
    </AuthContext.Provider>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getAuth: ApexAuth.authorization.actions.createAuthorization.request,
  logout: (params: { apexUser: boolean }) => {
    dispatch(
      legit.logout(LEGIT_SERVICE_CONFIG, {
        ...params,
      })
    );
  },
  clearLoginType: () => dispatch(ApexAuth.loginType.actions.clearLoginType()),
  getLegacyUrl: ApexAuth.legacyUrl.actions.fetchLegacyUrl
    .request as unknown as () => void, // to get around a type issue with typesafe-actions undefined for no params
});

const mapStateToProps = (state: RootState) => {
  return {
    auth: {
      hasBeenAsked:
        ApexAuth.authorization.selectors.authorizationHasBeenAsked(state),
      authorization: ApexAuth.authorization.selectors.authorizations(state),
      error: ApexAuth.authorization.selectors.error(state),
    },
    showAppContent:
      legit.selectors.selectShowLoginUI(state) === legit.LOGIN_UI_HIDE,
    token: apiToken(state),
  };
};

export const AuthProvider = connect(
  mapStateToProps,
  mapDispatchToProps
)(AuthProviderComponent);
